import { useContext, useEffect, useState } from "react";
import {
	Box,
	Button,
	Divider,
	IconButton,
	Modal,
	Typography,
} from "@mui/material";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import CityContext from "../../contexts/CityContext";
import { MapViewContext } from "../../contexts/MapViewContext";
import * as geoprocessor from "@arcgis/core/rest/geoprocessor";
import { cityReport } from "../../reports/cityReport";
import CloseIcon from "@mui/icons-material/Close";
import React from "react";
import JobInfo from "@arcgis/core/rest/support/JobInfo";
import {
	gpPDF_URL,
	chartColors,
	demoLabels,
	ageLabels,
	incomeLabels,
	demographicsInServiceArea,
	ageGroupInServiceArea,
	incomeGroupInServiceArea,
	placeSearchField,
	placeServiceIdField,
	dataFields
} from "../../constants";
import { chartOptions } from "../chartOptions";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { trackEvent } from "../../hooks/useAnalytics";

const modalStyle = {
	position: "absolute" as "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	width: 400,
	bgcolor: "background.paper",
	boxShadow: 24,
	p: 4,
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
};

export default function CityInfoForPanel() {
	ChartJS.register(
		CategoryScale,
		LinearScale,
		BarElement,
		Title,
		Tooltip,
		Legend
	);

	const { city, urban } = useContext(CityContext);

	// The `open` state and the two following functions are used to control whether or not the modal is displayed
	const [open, setOpen] = React.useState(false);
	const handleOpen = () => setOpen(true);
	const handleClose = () => setOpen(false);

	// These three state variables store data related to the GP Service request.
	// jobMessage is the current message returned from the GP Service while it is in progress and displayed to the user
	// pdfUrl is returned when the service is complete and is the location of the resulting report
	// gpSuccess handles whether the report request was successful or not
	const [jobMessage, setJobMessage] = React.useState<string>("");
	const [pdfUrl, setPdfUrl] = React.useState<string>("");
	const [gpSuccess, setGPSuccess] = React.useState(false);

	// The incomeData, ageData, and demoData state variables are used to store the respective datasets used to generate the charts
	const [incomeData, setIncomeData] = useState<any>({
		labels: incomeLabels,
		datasets: [
			{
				label: "income",
				data: [0, 0, 0],
				borderColor: chartColors,
				backgroundColor: chartColors,
			},
		],
	});
	const [ageData, setAgeData] = useState<any>({
		labels: ageLabels,
		datasets: [
			{
				label: "age",
				data: [0, 0, 0],
				borderColor: chartColors,
				backgroundColor: chartColors,
			},
		],
	});
	const [demoData, setDemoData] = useState<any>({
		labels: demoLabels,
		datasets: [
			{
				label: "demographics",
				data: [0, 0, 0, 0, 0, 0, 0, 0],
				borderColor: chartColors,
				backgroundColor: chartColors,
			},
		],
	});
	const mapView = useContext(MapViewContext);

	useEffect(() => {
		if (city) {
			setIncomeData({
				labels: incomeLabels,
				datasets: [
					{
						label: "",
						data: incomeGroupInServiceArea.map((x: any) =>
							city?.attributes[dataFields[x.value]] !== 0
								? Math.round(
									(city?.attributes[dataFields[x.value]] / city?.attributes[dataFields[x.total]]) *
											100
								  )
								: 0
						),
						borderColor: chartColors,
						backgroundColor: chartColors,
					},
				],
			});

			setAgeData({
				labels: ageLabels,
				datasets: [
					{
						label: "",
						data: ageGroupInServiceArea.map((x: any) =>
							city?.attributes[dataFields[x.value]] !== 0
								? Math.round(
									(city?.attributes[dataFields[x.value]] / city?.attributes[dataFields[x.total]]) *
											100
								  )
								: 0
						),
						borderColor: chartColors,
						backgroundColor: chartColors,
					},
				],
			});

			setDemoData({
				labels: demoLabels,
				datasets: [
					{
						label: "",
						data: demographicsInServiceArea.map((x: any) =>
							city?.attributes[dataFields[x.value]] !== 0
								? Math.round(
									(city?.attributes[dataFields[x.value]] / city?.attributes[dataFields[x.total]]) *
											100
								  )
								: 0
						),
						borderColor: chartColors,
						backgroundColor: chartColors,
					},
				],
			});
		}
	}, [city]);

	const convertAttributeFieldNames = (o: any) => {
		let fields = Object.keys(dataFields)
		let values = Object.values(dataFields)

		// update attribute fields to use SQL server names since hard-coded in python GP service  
		let stats: any = {}
		Object.keys(o).forEach((k) => {
			let key = k
			let i = values.indexOf(k)
			if (i >= 0) {
				key = fields[i]
			}
			stats[key] = o[k]
		})
		return stats
	}

	// This function submits the job and required parameters to the print service
	const submitJob = async () => {
		handleOpen();
		setGPSuccess(false);

		const newReport = cityReport;
		// area table
		newReport.tables.Area.body.fields[0].value = Math.round(
			city?.attributes[dataFields[newReport.tables.Population.body.fields[0].oname]]
		);
		// population table
		newReport.tables.Population.body.fields[0].value = Math.round(
			city?.attributes[dataFields[newReport.tables.Population.body.fields[0].oname]]
		);
		// age table
		newReport.tables.Age.body.fields.forEach((field, i) => {
			newReport.tables.Age.body.fields[i].value = Math.round(
				city?.attributes[dataFields[newReport.tables.Age.body.fields[i].oname]]
			);
		});
		// income table
		newReport.tables.Income.body.fields.forEach((field, i) => {
			newReport.tables.Income.body.fields[i].value = Math.round(
				city?.attributes[dataFields[newReport.tables.Income.body.fields[i].oname]]
			);
		});
		// race table
		newReport.tables.Race.body.fields.forEach((field, i) => {
			newReport.tables.Race.body.fields[i].value = Math.round(
				city?.attributes[dataFields[newReport.tables.Race.body.fields[i].oname]]
			);
		});
		// other parameters
		newReport.ProjectName = `${city?.attributes[placeSearchField]} - City Level Report`;
		newReport.CityOrUrbanID = city?.attributes[placeServiceIdField];

		//newReport.PlaceStats = city?.attributes;
		//newReport.UrbanStats = urban?.attributes;

		// update attribute fields to use SQL server names since hard-coded in python GP service 
		newReport.PlaceStats = convertAttributeFieldNames(city?.attributes);
		newReport.UrbanStats = convertAttributeFieldNames(urban?.attributes);
		

		const report_json = {
			report: newReport,
		};

		var gpParams = {
			INPUT_REPORT_JSON: JSON.stringify(report_json),
		};

		const jobInfo: JobInfo = await geoprocessor.submitJob(gpPDF_URL, gpParams);

		// The callback below monitors the progress of the GP Service to be communicated to the user
		const options = {
			interval: 3000,
			statusCallback: (j: any) => {
				if (j.messages.length) {
					setJobMessage(j.messages[j.messages.length - 1].description);
				} else {
					setJobMessage(
						"Unable to contact server.  Please wait or try again later."
					);
				}
				if (j.jobStatus === "esriJobFailed") {
					console.log("Error: Job failed.");
				}
			},
		};

		jobInfo.waitForJobCompletion(options).then((resp: any) => {
			getJobResultData(jobInfo);
		});

		// This function is triggered once the job is complete
		const getJobResultData = (jobInfo: JobInfo) => {
			jobInfo.fetchResultData("output_file").then((r: any) => {
				setGPSuccess(true);
				setPdfUrl(r.value.url);
			});
		};
	};

	return (
		<>
			<Typography
				sx={{ textAlign: "center" }}
				variant="h1"
				color="earth.main"
				gutterBottom
			>
				{city?.name}
			</Typography>
			<Box
				alignItems="center"
				justifyContent="center"
				className="right-panel__export-button"
			>
				<Button
					aria-label="Export Access Statistics PDF"
					onClick={() => {
						trackEvent("export_statistics_click", "city");
						//trackExportPDF("city");
						submitJob();
					}}
					variant="contained"
				>
					Export Access Statistics PDF
				</Button>
			</Box>
			<Divider sx={{ marginTop: "25px" }} />
			<Box
				display="flex"
				flexDirection="row"
				justifyContent="center"
				sx={{ marginTop: "15px", marginBottom: "5px" }}
			>
				<Box
					display="flex"
					flexDirection="column"
					justifyContent="center"
					maxWidth="210px"
					textAlign="center"
				>
					<Typography
						variant="h1"
						color="green"
					>
						{Math.round(
							(city?.attributes?.[dataFields["SUM_TOTPOPSVCA"]] /
								city?.attributes?.[dataFields["SUM_TPL_TOT_POP"]]) *
								100
						) + "%"}
					</Typography>
					<Typography
						variant="h3"
						color=""
					>
						of residents live within a 10-minute walk of a park
					</Typography>
				</Box>
			</Box>
			<Box
				display="flex"
				flexDirection="row"
				justifyContent="space-between"
				alignItems="flex-end"
				sx={{ marginTop: "10px", marginBottom: "5px" }}
			>
				<Box
					display="flex"
					flexDirection="column"
					alignItems="center"
				>
					<Typography
						variant="subtitle2"
						color=""
					>
						Population Served
					</Typography>
					<Typography
						variant="h3"
						color=""
					>
						{city?.attributes?.[dataFields["SUM_TOTPOPSVCA"]].toLocaleString("en-US")}
					</Typography>
				</Box>
				<Box
					display="flex"
					flexDirection="column"
					alignItems="center"
				>
					<Typography
						variant="subtitle2"
						color=""
					>
						Total Population of City
					</Typography>
					<Typography
						variant="h3"
						color=""
					>
						{city?.attributes?.[dataFields["SUM_TPL_TOT_POP"]].toLocaleString("en-US")}
					</Typography>
				</Box>
			</Box>
			<Divider sx={{ marginTop: "25px" }} />
			<Typography
				variant="h5"
				color="grass.main"
			>
				City Statistics
			</Typography>
			<Box
				display="flex"
				flexDirection="row"
				justifyContent="space-between"
				//alignItems="ce"
				sx={{ marginBottom: "5px" }}
			>
				<Box
					display="flex"
					flexDirection="column"
					alignItems="center"
				>
					<Typography
						variant="subtitle2"
						color=""
					>
						Total Number of Parks
					</Typography>
					<Typography
						variant="h3"
						color=""
					>
						{city?.attributes?.[dataFields["Park_Count"]]}
					</Typography>
				</Box>
				<Box
					display="flex"
					flexDirection="column"
					alignItems="center"
				>
					<Typography
						variant="subtitle2"
						color=""
					>
						Total Park Acres
					</Typography>
					<Typography
						variant="h3"
						color=""
					>
						{city?.attributes?.[dataFields["Total_Park_Acreage"]].toLocaleString("en-US")}
					</Typography>
				</Box>
			</Box>
			<Divider sx={{ marginTop: "25px" }} />
			<Typography
				variant="h5"
				color="grass.main"
				sx={{ marginBottom: "10px" }}
			>
				Race/Ethnicity
			</Typography>
			<Typography
				variant="subtitle2"
				color=""
			>
				Percentage of people within a 10-minute walk by race/ethnicity.
				*Excludes those that report Hispanic origin (which is captured
				separately from race by the U.S. Census).
			</Typography>
			<Divider />

			<Box
				height="400px"
				width="100%"
			>
				<Bar
					options={chartOptions(convertAttributeFieldNames(city?.attributes), demoData.labels)}
					plugins={[ChartDataLabels]}
					data={demoData}
				/>
			</Box>

			<Divider sx={{ marginTop: "25px" }} />
			<Typography
				variant="h5"
				color="grass.main"
				sx={{ marginBottom: "10px" }}
			>
				Age
			</Typography>
			<Typography
				variant="subtitle2"
				color=""
			>
				Percentage of people within a 10-minute walk by age group.
			</Typography>

			<Divider />
			<Box
				height="200px"
				width="100%"
			>
				<Bar
					options={chartOptions(convertAttributeFieldNames(city?.attributes), ageData.labels)}
					plugins={[ChartDataLabels]}
					data={ageData}
				/>
			</Box>

			<Divider sx={{ marginTop: "25px" }} />
			<Typography
				variant="h5"
				color="grass.main"
				sx={{ marginBottom: "10px" }}
			>
				Income
			</Typography>
			<Typography
				variant="subtitle2"
				color=""
			>
				Percentage of households within a 10-minute walk by income. MAI - Median
				Area Income, which is the median income of the urban area.
			</Typography>

			<Divider />
			<Box
				height="200px"
				width="100%"
				id="city_scroll"
			>
				<Bar
					options={chartOptions(convertAttributeFieldNames(city?.attributes), incomeData.labels)}
					plugins={[ChartDataLabels]}
					data={incomeData}
				/>
			</Box>

			<Divider sx={{ marginTop: "25px" }} />

			<Typography
				textAlign="left"
				variant="h6"
				color="headingGrey.main"
			>
				Demographic information sourced from Esri 2025
			</Typography>
			<Modal
				open={open}
				onClose={handleClose}
				aria-labelledby="modal-modal-title"
				aria-describedby="modal-modal-description"
			>
				<Box sx={modalStyle}>
					<IconButton
						aria-label="close"
						onClick={handleClose}
						sx={{
							position: "absolute",
							right: 8,
							top: 8,
							color: (theme) => theme.palette.grey[500],
						}}
					>
						<CloseIcon />
					</IconButton>

					{!gpSuccess && (
						<>
							<Typography
								id="modal-modal-title"
								variant="h3"
								component="h2"
							>
								Generating PDF report...
							</Typography>
							<Typography
								id="modal-modal-description"
								sx={{ mt: 2 }}
							>
								{jobMessage}
							</Typography>
						</>
					)}
					{gpSuccess && (
						<>
							<Typography
								id="modal-modal-title"
								variant="h3"
								component="h2"
							>
								Success!
							</Typography>
							<Button
								variant="contained"
								sx={{ marginTop: "10px" }}
								href={pdfUrl}
								target="_blank"
								onClick={() => {
									trackEvent("export_statistics_pdf", "city");
								}}
							>
								Download PDF Report
							</Button>
						</>
					)}
				</Box>
			</Modal>
		</>
	);
}
